import React from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { X, Shield, FileText, Scale, ArrowLeft } from 'lucide-react';
import { Dialog, DialogContent, DialogTrigger, DialogTitle } from './ui/dialog';

// Legal content translations
const legalContent = {
  ar: {
    disclaimer: {
      title: 'إخلاء المسؤولية',
      content: `
## إخلاء المسؤولية القانونية

**آخر تحديث: يناير 2025**

### 1. المعلومات العامة

المعلومات المقدمة على هذا الموقع الإلكتروني هي لأغراض إعلامية عامة فقط. بينما نسعى للحفاظ على دقة المعلومات وتحديثها، فإننا لا نقدم أي تعهدات أو ضمانات من أي نوع، صريحة أو ضمنية، حول اكتمال أو دقة أو موثوقية أو ملاءمة أو توفر المعلومات.

### 2. لا يشكل مشورة قانونية

المحتوى المقدم على هذا الموقع لا يشكل مشورة قانونية ولا ينبغي الاعتماد عليه كذلك. كل حالة فريدة ونتائج القضايا تعتمد على ظروفها الخاصة. للحصول على مشورة قانونية محددة، يرجى التواصل معنا مباشرة.

### 3. نتائج القضايا السابقة

النتائج المعروضة للقضايا السابقة لا تضمن نتائج مماثلة في القضايا المستقبلية. كل قضية تُقيّم بناءً على ظروفها الفردية.

### 4. الروابط الخارجية

قد يحتوي موقعنا على روابط لمواقع خارجية. نحن لا نتحكم في محتوى هذه المواقع ولا نتحمل مسؤولية أي محتوى أو ممارسات خصوصية تتبعها.

### 5. التغييرات على هذا الإخلاء

نحتفظ بالحق في تعديل إخلاء المسؤولية هذا في أي وقت. ستكون التغييرات سارية فور نشرها على الموقع.

### 6. الاتصال بنا

إذا كانت لديك أي أسئلة حول إخلاء المسؤولية هذا، يرجى التواصل معنا عبر البريد الإلكتروني: info@qasimlaw.com

---

**مكتب المحامي د. قاسم حسين**
لندن، المملكة المتحدة
      `
    },
    privacy: {
      title: 'سياسة الخصوصية',
      content: `
## سياسة الخصوصية

**آخر تحديث: يناير 2025**

### 1. مقدمة

نحن في مكتب المحامي د. قاسم حسين نلتزم بحماية خصوصيتك. توضح هذه السياسة كيفية جمع واستخدام وحماية معلوماتك الشخصية.

### 2. المعلومات التي نجمعها

نقوم بجمع المعلومات التالية:
- **معلومات الاتصال**: الاسم، البريد الإلكتروني، رقم الهاتف
- **تفاصيل القضية**: المبالغ المفقودة، أسماء الشركات المحتالة، الوثائق ذات الصلة
- **معلومات تقنية**: عنوان IP، نوع المتصفح، معلومات الجهاز

### 3. كيف نستخدم معلوماتك

نستخدم معلوماتك من أجل:
- تقييم قضيتك وتقديم المشورة القانونية
- التواصل معك بشأن خدماتنا
- تحسين موقعنا وخدماتنا
- الامتثال للمتطلبات القانونية

### 4. مشاركة المعلومات

لا نبيع أو نؤجر معلوماتك الشخصية لأطراف ثالثة. قد نشارك معلوماتك مع:
- المحاكم والجهات القضائية عند الضرورة
- مقدمي الخدمات الذين يساعدوننا في عملنا
- السلطات القانونية عند الطلب

### 5. أمن البيانات

نتخذ تدابير أمنية مناسبة لحماية معلوماتك من الوصول غير المصرح به أو التعديل أو الإفصاح أو الإتلاف.

### 6. حقوقك

لديك الحق في:
- الوصول إلى معلوماتك الشخصية
- تصحيح المعلومات غير الدقيقة
- طلب حذف بياناتك
- الاعتراض على معالجة بياناتك

### 7. ملفات تعريف الارتباط (Cookies)

نستخدم ملفات تعريف الارتباط لتحسين تجربتك على موقعنا. يمكنك التحكم في إعدادات ملفات تعريف الارتباط من خلال متصفحك.

### 8. التواصل معنا

لأي استفسارات حول سياسة الخصوصية:
- البريد الإلكتروني: info@qasimlaw.com
- الهاتف: +44 20 1234 5678

---

**مكتب المحامي د. قاسم حسين**
لندن، المملكة المتحدة
      `
    },
    terms: {
      title: 'الشروط والأحكام',
      content: `
## الشروط والأحكام

**آخر تحديث: يناير 2025**

### 1. قبول الشروط

باستخدامك لهذا الموقع، فإنك توافق على الالتزام بهذه الشروط والأحكام. إذا كنت لا توافق على أي جزء منها، يرجى عدم استخدام الموقع.

### 2. الخدمات المقدمة

يقدم مكتب المحامي د. قاسم حسين خدمات قانونية متخصصة في:
- استرداد الأموال من شركات التداول المحتالة
- التمثيل القانوني أمام المحاكم البريطانية والدولية
- الاستشارات القانونية في قضايا الاحتيال المالي

### 3. الاستشارة الأولية المجانية

الاستشارة الأولية مجانية ولا تُنشئ علاقة محامي-موكل تلقائياً. تبدأ العلاقة الرسمية بعد توقيع اتفاقية التمثيل القانوني.

### 4. الأتعاب والمدفوعات

**نظام "لا ربح، لا أتعاب":**
- لا تُدفع أي أتعاب مقدماً في معظم القضايا
- يتم تحديد نسبة الأتعاب في اتفاقية مكتوبة
- تُستحق الأتعاب فقط عند نجاح القضية واسترداد الأموال

### 5. السرية

نلتزم بالحفاظ على سرية جميع المعلومات التي تشاركها معنا وفقاً لقواعد السرية المهنية للمحاماة.

### 6. حدود المسؤولية

- لا نضمن نتائج محددة لأي قضية
- مسؤوليتنا محدودة بما يسمح به القانون
- النتائج تعتمد على الظروف الفردية لكل قضية

### 7. الملكية الفكرية

جميع المحتويات على هذا الموقع، بما في ذلك النصوص والرسومات والشعارات، هي ملكية حصرية لمكتب المحامي د. قاسم حسين.

### 8. التعديلات

نحتفظ بالحق في تعديل هذه الشروط في أي وقت. سيتم نشر التعديلات على هذه الصفحة.

### 9. القانون الحاكم

تخضع هذه الشروط لقوانين إنجلترا وويلز، وتختص محاكمها بالنظر في أي نزاع.

### 10. التواصل معنا

للاستفسارات حول هذه الشروط:
- البريد الإلكتروني: info@qasimlaw.com
- العنوان: لندن، المملكة المتحدة

---

**مكتب المحامي د. قاسم حسين**
مرخص من هيئة تنظيم المحامين (SRA)
      `
    }
  },
  en: {
    disclaimer: {
      title: 'Disclaimer',
      content: `
## Legal Disclaimer

**Last Updated: January 2025**

### 1. General Information

The information provided on this website is for general informational purposes only. While we strive to keep the information accurate and up-to-date, we make no representations or warranties of any kind, express or implied, about the completeness, accuracy, reliability, suitability, or availability of the information.

### 2. Not Legal Advice

The content provided on this site does not constitute legal advice and should not be relied upon as such. Each case is unique and outcomes depend on specific circumstances. For specific legal advice, please contact us directly.

### 3. Past Case Results

Results displayed from past cases do not guarantee similar outcomes in future cases. Each case is evaluated based on its individual circumstances.

### 4. External Links

Our website may contain links to external sites. We do not control the content of these sites and are not responsible for any content or privacy practices they follow.

### 5. Changes to This Disclaimer

We reserve the right to modify this disclaimer at any time. Changes will be effective immediately upon posting on the website.

### 6. Contact Us

If you have any questions about this disclaimer, please contact us via email: info@qasimlaw.com

---

**Dr. Qasim Hussein Law Firm**
London, United Kingdom
      `
    },
    privacy: {
      title: 'Privacy Policy',
      content: `
## Privacy Policy

**Last Updated: January 2025**

### 1. Introduction

At Dr. Qasim Hussein Law Firm, we are committed to protecting your privacy. This policy explains how we collect, use, and protect your personal information.

### 2. Information We Collect

We collect the following information:
- **Contact Information**: Name, email, phone number
- **Case Details**: Lost amounts, fraudulent company names, relevant documents
- **Technical Information**: IP address, browser type, device information

### 3. How We Use Your Information

We use your information to:
- Evaluate your case and provide legal advice
- Communicate with you about our services
- Improve our website and services
- Comply with legal requirements

### 4. Information Sharing

We do not sell or rent your personal information to third parties. We may share your information with:
- Courts and judicial authorities when necessary
- Service providers who assist us in our work
- Legal authorities upon request

### 5. Data Security

We take appropriate security measures to protect your information from unauthorized access, alteration, disclosure, or destruction.

### 6. Your Rights

You have the right to:
- Access your personal information
- Correct inaccurate information
- Request deletion of your data
- Object to processing of your data

### 7. Cookies

We use cookies to improve your experience on our website. You can control cookie settings through your browser.

### 8. Contact Us

For any privacy policy inquiries:
- Email: info@qasimlaw.com
- Phone: +44 20 1234 5678

---

**Dr. Qasim Hussein Law Firm**
London, United Kingdom
      `
    },
    terms: {
      title: 'Terms & Conditions',
      content: `
## Terms & Conditions

**Last Updated: January 2025**

### 1. Acceptance of Terms

By using this website, you agree to be bound by these terms and conditions. If you do not agree to any part of them, please do not use the website.

### 2. Services Provided

Dr. Qasim Hussein Law Firm provides specialized legal services in:
- Fund recovery from fraudulent trading companies
- Legal representation in British and international courts
- Legal consultations in financial fraud cases

### 3. Free Initial Consultation

The initial consultation is free and does not automatically create an attorney-client relationship. The official relationship begins after signing the legal representation agreement.

### 4. Fees and Payments

**"No Win, No Fee" System:**
- No upfront fees in most cases
- Fee percentage is determined in a written agreement
- Fees are only due upon case success and fund recovery

### 5. Confidentiality

We commit to maintaining the confidentiality of all information you share with us in accordance with professional legal confidentiality rules.

### 6. Limitation of Liability

- We do not guarantee specific outcomes for any case
- Our liability is limited to what the law allows
- Results depend on individual case circumstances

### 7. Intellectual Property

All content on this website, including text, graphics, and logos, is the exclusive property of Dr. Qasim Hussein Law Firm.

### 8. Modifications

We reserve the right to modify these terms at any time. Modifications will be posted on this page.

### 9. Governing Law

These terms are governed by the laws of England and Wales, and its courts have jurisdiction over any disputes.

### 10. Contact Us

For inquiries about these terms:
- Email: info@qasimlaw.com
- Address: London, United Kingdom

---

**Dr. Qasim Hussein Law Firm**
Licensed by the Solicitors Regulation Authority (SRA)
      `
    }
  }
};

// Get content for other languages (fallback to English)
const getContent = (language, type) => {
  if (legalContent[language] && legalContent[language][type]) {
    return legalContent[language][type];
  }
  return legalContent.en[type];
};

const LegalPageDialog = ({ type, children }) => {
  const { language, direction } = useLanguage();
  const content = getContent(language, type);
  
  const icons = {
    disclaimer: Scale,
    privacy: Shield,
    terms: FileText
  };
  
  const IconComponent = icons[type] || FileText;

  return (
    <Dialog>
      <DialogTrigger asChild>
        {children}
      </DialogTrigger>
      <DialogContent 
        className="max-w-4xl max-h-[90vh] overflow-hidden bg-white p-0"
        aria-describedby={`${type}-content`}
      >
        <DialogTitle className="sr-only">{content.title}</DialogTitle>
        
        {/* Header */}
        <div className="sticky top-0 z-10 bg-gradient-to-r from-[#0A1628] to-[#1a2942] px-6 py-5 border-b border-[#C9A55A]/20">
          <div className={`flex items-center gap-4 ${direction === 'rtl' ? 'flex-row-reverse' : ''}`}>
            <div className="w-12 h-12 bg-[#C9A55A]/20 rounded-xl flex items-center justify-center">
              <IconComponent className="w-6 h-6 text-[#C9A55A]" />
            </div>
            <div className={direction === 'rtl' ? 'text-right' : ''}>
              <h2 className="text-2xl font-bold text-white" style={{ fontFamily: 'Georgia, serif' }}>
                {content.title}
              </h2>
              <p className="text-sm text-gray-400">
                {direction === 'rtl' ? 'مكتب المحامي د. قاسم حسين' : 'Dr. Qasim Hussein Law Firm'}
              </p>
            </div>
          </div>
        </div>
        
        {/* Content */}
        <div 
          id={`${type}-content`}
          className={`p-6 overflow-y-auto max-h-[calc(90vh-100px)] ${direction === 'rtl' ? 'text-right' : ''}`}
          dir={direction}
        >
          <div className="prose prose-lg max-w-none prose-headings:text-[#0A1628] prose-headings:font-bold prose-p:text-gray-700 prose-li:text-gray-700 prose-strong:text-[#0A1628]">
            {content.content.split('\n').map((line, idx) => {
              if (line.startsWith('## ')) {
                return <h2 key={idx} className="text-2xl mt-6 mb-4 text-[#0A1628]">{line.replace('## ', '')}</h2>;
              }
              if (line.startsWith('### ')) {
                return <h3 key={idx} className="text-xl mt-5 mb-3 text-[#0A1628]">{line.replace('### ', '')}</h3>;
              }
              if (line.startsWith('**') && line.endsWith('**')) {
                return <p key={idx} className="font-bold text-gray-600 my-2">{line.replace(/\*\*/g, '')}</p>;
              }
              if (line.startsWith('- ')) {
                return <li key={idx} className="ml-4 my-1">{line.replace('- ', '')}</li>;
              }
              if (line.startsWith('---')) {
                return <hr key={idx} className="my-6 border-[#C9A55A]/30" />;
              }
              if (line.trim() === '') {
                return <br key={idx} />;
              }
              return <p key={idx} className="my-2">{line}</p>;
            })}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default LegalPageDialog;
